import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@/App/hooks';
import { DataState } from '@/App/types';
import { ActionRef } from '@/App/types/actionRef';
import { Data, DataObject } from '@/App/types/ruleEngineTypes';
import { ScreenRef } from '@/App/types/screenRef';
import { DataType } from '@/features/displayRules';
import { getDisplayRulesOutputThunk, runMetadataRules, runStateRules } from '@/features/displayRules/displayRulesSlice';
import { selectDisplayRulesDataState } from '@/features/displayRules/selectDisplayRulesDataState';
import { selectLocale } from '@/features/i18n/selectLocale';
import { getReferenceDataMapThunk, setReferenceDataRefresh } from '@/features/refdata/refdataSlice';
import { selectRefDataInitialized } from '@/features/refdata/selectRefDataInitialized';
import { selectRefDataRefresh } from '@/features/refdata/selectRefDataRefresh';

export interface PageFragmentProps {
  screenRef: ScreenRef;
  actionRef: ActionRef;
}

export interface WithPageFragmentProps extends PageFragmentProps {
  doInit?: boolean;

  getData(): DataType;
}

export type PageFragmentInitProps = WithPageFragmentProps & Data;

export const withPageFragmentInit = <ExtraProps extends object = {}>(
  Component: React.ComponentType<WithPageFragmentProps & ExtraProps>,
) => {
  const HOC: React.FC<WithPageFragmentProps & ExtraProps> = (props) => {
    const { screenRef, actionRef, getData, doInit = true, ...rest } = props;
    const dispatch = useAppDispatch();
    const locale = useAppSelector(selectLocale);
    const refDataRefresh = useAppSelector(selectRefDataRefresh);
    const refDataInitialized = useAppSelector(selectRefDataInitialized(screenRef));
    const displayRulesDataState = useAppSelector(selectDisplayRulesDataState(screenRef, actionRef));
    const data = getData();

    useEffect(() => {
      if (doInit && locale && (!refDataInitialized || refDataRefresh)) {
        dispatch(setReferenceDataRefresh(false));
        dispatch(getReferenceDataMapThunk({ screenRef, locale }));
      }
    }, [dispatch, refDataInitialized, screenRef, doInit, locale, refDataRefresh]);

    useEffect(() => {
      if (doInit && displayRulesDataState === DataState.INITIAL) {
        dispatch(getDisplayRulesOutputThunk({ screen: screenRef, action: actionRef }));
      }
    }, [dispatch, displayRulesDataState, screenRef, actionRef, doInit]);

    useEffect(() => {
      if (displayRulesDataState === DataState.INITIALIZED) {
        const dataObject = data as unknown as DataObject;
        dispatch(
          runMetadataRules({
            screen: screenRef,
            action: actionRef,
            data: dataObject,
          }),
        );
        dispatch(
          runStateRules({
            screen: screenRef,
            action: actionRef,
            data: dataObject,
          }),
        );
      }
    }, [dispatch, displayRulesDataState, data, screenRef, actionRef]);

    if (!refDataInitialized || refDataRefresh || displayRulesDataState !== DataState.INITIALIZED) {
      return null;
    }

    return (
      <Component
        screenRef={screenRef}
        actionRef={actionRef}
        getData={getData}
        doInit={doInit}
        {...(rest as ExtraProps)}
      />
    );
  };
  HOC.displayName = `withPageFragmentInit(${Component.displayName || Component.name || 'Component'})`;
  return HOC;
};
